import React from "react";
import { compose, withAppContext, withActions } from "containers/HOC";
import { push } from "connected-react-router";
import * as utils from "common/utils";
import Card from "components/new/Card";
import CardHeader from "components/new/CardHeader";
import CardHeaderColumn from "components/new/CardHeaderColumn";
import CardHeaderField from "components/new/CardHeaderField";
import CardActions from "components/new/CardActions";
import CardActionStatus from "components/new/CardActionStatus";
import CardActionsLeft from "components/new/CardActionsLeft";
import CardActionsRight from "components/new/CardActionsRight";
import ActionLink from "components/ActionLink";
import AnalysisResultsWidget from "components/ResultsWidgets/AnalysisResultsWidget";
import { modal, confirm } from "containers/App/_actions";
import { createWorkingCopy, deletePublication, setAsPlan } from "./_actions";
import { ManagePublicationActionLink, AddPublicationCommentActionLink, ViewPublicationHistoryActionLink, isPublicationLocked, isPublishingToMPIWholeFarmDataProject } from "containers/Publications";

const PublicationCard = ({ publication, farm, online, push, modal, confirm, createWorkingCopy, deletePublication, budget, isLoading, authContext, setAsPlan }) => {
    const workingCopy = publication.workingCopyId && (farm.offlineBudgets || []).find((ob) => ob.budget.id === publication.workingCopyId);
    const offlinePa = publication.publishedAnalysisId && (farm.offlineBudgets || []).find((ob) => ob.budget.id === publication.publishedAnalysisId);
    const publishedAnalysis = offlinePa && offlinePa.budget;

    const isLocked = isPublicationLocked(publication);
    const canManage = online && !isLocked;
    const canViewHistory = online;
    const canViewWorkingCopy = online && publication.hasPublisheeAccess && publication.accountId !== publication.publishedBy.accountId && !publication.farmGroupId;
    const canAddComment = online;
    const isPublishedByMe = publication.publishedBy.accountId === authContext.accountId;
    const isPublishedToMe = publication.accountId === authContext.accountId;
    const isPublishedToAFarmGroup = publication.farmGroupId && !publication.accountId;
    const isPublishingToMPI = isPublishingToMPIWholeFarmDataProject(publication.accountId);
    const iAmAnOwnerOfTheFarm = farm.role === "Owner";
    const canDelete = online && (authContext.isSystemAdmin || isPublishedToMe || (iAmAnOwnerOfTheFarm && isPublishedToAFarmGroup) || (isPublishedByMe && isPublishingToMPI));

    const _viewPublication = () => {
        push(`/app/farm/${farm.id}/analysis/${publication.publishedAnalysisId}/overview`);
    };

    const _viewWorkingCopy = () => {
        if (workingCopy) {
            push(`/app/farm/${farm.id}/analysis/${workingCopy.id}`);
        } else {
            if (!isLoading) createWorkingCopy(publication);
        }
    };

    const _deletePublication = () => {
        confirm(`Are you sure you want to delete the "Version ${publication.version}" publication of the "${budget.name}" analysis?`, () => deletePublication(publication), false);
    };

    const showPreviousResults = !publishedAnalysis.previousNLossPerHa || publishedAnalysis.previousNLossPerHa === 0 ? false : publishedAnalysis.nLossPerHa === publication.previousNLossPerHa && publication.pLossPerHa === publishedAnalysis.previousPLossPerHa && publishedAnalysis.ghgkPerHa > 0 && publishedAnalysis.ghgkPerHa === publishedAnalysis.previousGHGKPerHa ? false : true;
    const previousMessage = !publishedAnalysis.previousNLossPerHa || publishedAnalysis.previousNLossPerHa === 0 ? `v${publishedAnalysis.previousModelVersion}: no results` : publication.nLossPerHa === publication.previousNLossPerHa && publishedAnalysis.pLossPerHa === publishedAnalysis.previousPLossPerHa && publishedAnalysis.ghgkPerHa > 0 && publishedAnalysis.ghgkPerHa === publishedAnalysis.previousGHGKPerHa ? `v${publishedAnalysis.previousModelVersion}: no change` : `v${publishedAnalysis.previousModelVersion}: `;
    const canSetAsPlan = budget.type === "Predictive" && publication.accountId === authContext.accountId && (authContext.isSystemAdmin || authContext.orgApiAccess);

    let statusIcon = "u-textSecondary";
    switch (publication.status) {
        case "Draft":
            statusIcon = "u-textSecondary";
            break;
        case "Submitted":
        case "Processed":
            statusIcon = "u-textSuccess";
            break;
        case "Returned":
        case "Reviewing":
            statusIcon = "u-textWarning";
            break;
        default:
            statusIcon = "u-textMidGrey";
    }

    return (
        <>
            <Card key={publication.id}>
                <input type="hidden" id={`${budget.id}_${publication.version}`} value={publication.id} />
                <CardHeader dataWidth="md-100 xl-30" title={`${publication.publishedTo}`} subTitle={`Version ${publication.version}`} onTitleClick={online ? _viewPublication : undefined} onHeaderClick={online ? _viewPublication : undefined} id={publication.id} name={publication.reference}>
                    <CardHeaderColumn dataWidth="md-15 xl-10">
                        <CardHeaderField label="Published" value={utils.dateOnly(publication.publishedOn)} />
                    </CardHeaderColumn>
                    <CardHeaderColumn dataWidth="md-15 xl-10">
                        <CardHeaderField label="Published by" value={publication.publishedBy ? publication.publishedBy.accountName : "-"} />
                    </CardHeaderColumn>
                    <CardHeaderColumn dataWidth="md-15 xl-10">
                        <CardHeaderField label="Farm identifier" value={publication.farmIdentifier ? publication.farmIdentifier : "-"} />
                    </CardHeaderColumn>
                    <CardHeaderColumn dataWidth="md-15 xl-10">
                        <CardHeaderField label="Reference" value={publication.reference ? publication.reference : "-"} />
                    </CardHeaderColumn>
                    <CardHeaderColumn nutrientsLost>
                        <AnalysisResultsWidget farm={farm} analysis={publishedAnalysis} perHaOnly />
                    </CardHeaderColumn>
                </CardHeader>
                <CardActions>
                    <CardActionsLeft>
                        {online && (
                            <React.Fragment>
                                <CardActionStatus className={"icon icon-dot " + statusIcon} status={publication.status} />
                            </React.Fragment>
                        )}
                        <React.Fragment>
                            <CardActionStatus status={previousMessage} />
                            {showPreviousResults && (
                                <React.Fragment>
                                    {publishedAnalysis.previousNLossPerHa ? <CardActionStatus className="icon icon-square N-color" status={`N/ha: ${publishedAnalysis.previousNLossPerHa}`} /> : ""}
                                    {publishedAnalysis.previousPLossPerHa ? <CardActionStatus className="icon icon-square P-color" status={`P/ha: ${publishedAnalysis.previousPLossPerHa}`} /> : ""}
                                    {publishedAnalysis.previousGHGKPerHa ? <CardActionStatus className="icon icon-square GHG-color" status={`GHG/ha: ${publishedAnalysis.previousGHGKPerHa}`} /> : ""}
                                </React.Fragment>
                            )}
                        </React.Fragment>
                        {!online && (
                            <React.Fragment>
                                <CardActionStatus className="icon icon-cross-circle u-textError" status="Offline" />
                            </React.Fragment>
                        )}
                    </CardActionsLeft>
                    <CardActionsRight>
                        {online && (
                            <ul className="ActionsBar-links">
                                {canSetAsPlan && (
                                    <li>
                                        {publication.isPlan ? (
                                            <span className="IconLink--target u-font-green">Is plan</span>
                                        ) : (
                                            <ActionLink id={`set-plan-${budget.id}`} className="IconLink--target" onClick={() => setAsPlan(farm.id, publication.id)}>
                                                Set as plan
                                            </ActionLink>
                                        )}
                                    </li>
                                )}
                                {canManage && (
                                    <li>
                                        <ManagePublicationActionLink publication={publication} />
                                    </li>
                                )}
                                {canViewHistory && (
                                    <li>
                                        <ViewPublicationHistoryActionLink publication={publication} />
                                    </li>
                                )}
                                {canViewWorkingCopy && (
                                    <li>
                                        <ActionLink id={`view-working-copy-${publication.id}`} className="IconLink--edit" onClick={_viewWorkingCopy}>
                                            Working copy
                                        </ActionLink>
                                    </li>
                                )}
                                {canAddComment && (
                                    <li>
                                        <AddPublicationCommentActionLink publication={publication} />
                                    </li>
                                )}
                                {canDelete && (
                                    <li>
                                        <ActionLink id={`delete-publication-${publication.id}`} className="IconLink--cross-circle" onClick={_deletePublication}>
                                            Delete
                                        </ActionLink>
                                    </li>
                                )}
                            </ul>
                        )}
                    </CardActionsRight>
                </CardActions>
            </Card>
        </>
    );
};

export default compose(withAppContext, withActions({ push, modal, confirm, createWorkingCopy, deletePublication, setAsPlan }))(PublicationCard);
